import React, {useState, useEffect} from "react";
import DropdownMenu from "../../components/dropdownMenu";
import styles from "./Profile.module.scss";
import stylesPractice from "./Practice.module.scss";
import {useTranslation} from "react-i18next";
import {useActions} from "../../hook/useActions";
import {useNavigate} from "react-router-dom";
import {useTypedSelector} from "../../hook/useTypedSelector";
import {Interface} from "node:readline";
import PostResult from "../../components/post-result";
import HelpList from "../../components/help-list";
import {ErrorMessage, Field, Formik} from "formik";
import ProgressBar from "../../components/ProgressBar";
import GaurabdaCalendar from "../../components/gcalendar";
import EkadashiListV2 from "../ekadashiListV2";
import {loadOldCredentials} from "../../services/api-user-service";
import {jwtDecode} from "jwt-decode";

export enum ProfileDropdowns {
    PROFILE = "PROFILE",
    HELP_LIST = "HELP_LIST",
    REPORT = "REPORT",
    RESULTS = "RESULTS",
    GCAL = "GCAL",
    EKADASHI_LIST = "EKADASHI_LIST",
    NONE = "NONE"
}

interface ProfileProps {
    preOpenDropdown?: ProfileDropdowns;
}

const itemWeights: { [key: string]: number } = {
    item_10_1: 5,
    item_10_2: 5,
    item_10_3: 5,
    item_11_1: 5,
    item_11_2: 10,
    item_11_3: 20,
    item_11_4: 10,
    item_11_5: 10,
    item_11_6: 20,
    item_11_7: 5,
    item_12_1: 5,
};

class ItemList {
    item_10_1: boolean = false;
    item_10_2: boolean = false;
    item_10_3: boolean = false;
    item_11_1: boolean = false;
    item_11_2: boolean = false;
    item_11_3: boolean = false;
    item_11_4: boolean = false;
    item_11_5: boolean = false;
    item_11_6: boolean = false;
    item_11_7: boolean = false;
    item_12_1: boolean = false;
};

const Profile: React.FC<ProfileProps> = ({preOpenDropdown = ProfileDropdowns.NONE}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {LogOut, ProfileAsync, StatusAsync, GetPostReport, GetPostPracticeCalendar, SendReport, ChangePasswordAsync, UnloadForceLogin} = useActions();
    const {profile, status, isAuth, user} = useTypedSelector((state) => state.user);
    const {postPracticeCalendar} = useTypedSelector((state) => state.calendar);
    const {postReport} = useTypedSelector((state) => state.postPractice);
    const [selectedPercent, setSelectedPercent] = useState<string>("");
    const [savedCity, setSavedCity] = useState<string>("");
    const [savedCountry, setSavedCountry] = useState<string>("");
    const [oldUserEmail, setOldUserEmail] = useState<string|null>(null);
    const [canSendReport, setCanSendReport] = useState<boolean>(false);
    const [repDate, setRepDate] = useState<Date | null>(null);
    const [isRefNumberCopied, setIsRefNumberCopied] = useState<boolean>(false);

    const [isPostReportChanged, setIsPostReportChanged] = useState<boolean>(false);
    const [isPostReportTouched, setIsPostReportTouched] = useState<boolean>(false);

    const [checkedHints, setCheckedHints] = useState<ItemList>(new ItemList());


    useEffect(() => {
        ProfileAsync();
        StatusAsync();

        let country = localStorage.getItem("country") ?? "Ukraine";
        let city = localStorage.getItem("city") ?? "Rivne";
        setSavedCity(city);
        setSavedCountry(country);
        GetPostReport(city, country);

        const tokens = loadOldCredentials();
        if(tokens) {
            const decodedToken = jwtDecode(tokens.accessToken) as any;
            setOldUserEmail(decodedToken.email);
        }
    }, []);

    useEffect(() => {
        if (!(postReport && postReport.date && savedCity && savedCountry)) {
            return;
        }

        let date = new Date(postReport.date);
        date.setDate(date.getDate() - 1);

        const data = {
            city: savedCity,
            country: savedCountry,
            period: 3,
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            day: date.getDate()
        }

        GetPostPracticeCalendar(data);

        if (postReport.percentCompleated !== 0) {
            setSelectedPercent(postReport.percentCompleated + "%");
        } else {
            setSelectedPercent("");
        }

        let dateNow = new Date();
        dateNow.setHours(0, 0, 0, 0);
        let reportDate = new Date(postReport.date);
        reportDate.setDate(reportDate.getDate() - 1);
        reportDate.setHours(0, 0, 0, 0);
        setCanSendReport(dateNow >= reportDate);
        setRepDate(reportDate);

        const prl = JSON.parse(postReport.postReportListJSON);
        if (prl !== null) {
            const parsedList: ItemList = Object.assign(new ItemList(), prl);
            setCheckedHints(parsedList);
        }
    }, [postReport, savedCity, savedCountry]);

    // Helper to handle hint image click
    const handleHintClick = (itemKey: keyof ItemList) => {
        setCheckedHints((prev) => {
            const updated = new ItemList();
            Object.assign(updated, prev); // copy previous values

            updated[itemKey] = !prev[itemKey]; // toggle

            setIsPostReportChanged(true);
            setIsPostReportTouched(true);

            return updated;
        });
    };

    // Calculate total weight of checked items
    const checkedWeightSum = Object.entries(checkedHints)
        .filter(([key, checked]) => checked && itemWeights[key])
        .reduce((sum, [key]) => sum + itemWeights[key], 0);

    function handleSendReportClick() {
        if (checkedWeightSum > 0 && checkedWeightSum <= 100) {
            SendReport({
                city: savedCity,
                country: savedCountry,
                postReportListJSON: JSON.stringify(checkedHints),
                percentCompleated: checkedWeightSum,
                date: postReport.date
            });
        }
        setIsPostReportChanged(false);
    }

    if (!isAuth) {
        return (<div className={styles.profileContainer}>
            <div className={styles.profileNotAuth}>
                <div className={styles.notAuthText}>
                  <span>
                    <span>
                      {t("Profile.BlessedFastsTitle")}
                        <br/>
                    </span>
                    <span>
                      {t("Profile.BlessedFastsSolve")}
                        <br/>
                    </span>
                    <span>
                      {t("Profile.BlessedFastsEkadashiDays")}
                        <br/>
                    </span>
                    <span>
                      {t("Profile.BlessedFastsEleventhDay")}
                    </span>
                  </span>
                </div>

                <GaurabdaCalendar/>
                <EkadashiListV2/>
            </div>
        </div>);
    }

    function handleBackToAdminPanelClick() {
        UnloadForceLogin();
        navigate("/admin/reports");
    }

    return (
        <div className={styles.profileContainer}>
            {oldUserEmail &&
                <div>
                    <div>You have logged in as other user.</div>
                    <button onClick={handleBackToAdminPanelClick}>Back to {oldUserEmail}!</button>
                </div>}
            <div className={styles.notAuthText} style={{marginBottom: "20px", marginTop: "10px"}}>
                  <span>
                    <span>
                      {t("Profile.BlessedFastsTitle")}
                        <br/>
                    </span>
                    <span>
                      {t("Profile.BlessedFastsSolve")}
                        <br/>
                    </span>
                    <span>
                      {t("Profile.BlessedFastsEkadashiDays")}
                        <br/>
                    </span>
                    <span>
                      {t("Profile.BlessedFastsEleventhDay")}
                    </span>
                  </span>
            </div>
            <DropdownMenu iconPath={"/images/icons/dropdowns/gcal.png"} label={t("Profile.GCAL")}
                          preOpened={preOpenDropdown === ProfileDropdowns.GCAL} preOpenedDropdown={preOpenDropdown}>
                <GaurabdaCalendar/>
            </DropdownMenu>
            <DropdownMenu iconPath={"/images/icons/dropdowns/moonLogo.png"} label={t("Profile.EkadashiList")}
                          preOpened={preOpenDropdown === ProfileDropdowns.EKADASHI_LIST}
                          preOpenedDropdown={preOpenDropdown}>
                <EkadashiListV2/>
            </DropdownMenu>
            <DropdownMenu iconPath={"/images/icons/dropdowns/profile.png"} label={t("Profile.Profile")}
                          preOpened={preOpenDropdown === ProfileDropdowns.PROFILE}
                          preOpenedDropdown={preOpenDropdown}>
                <Formik
                    initialValues={{oldPassword: "", newPassword: "", confirmNewPassword: ""}}
                    validate={values => {
                        const errors: { newPassword?: string, confirmNewPassword?: string; oldPassword?: string } = {};
                        if (!values.oldPassword) {
                            errors.oldPassword = t("Register.Validation.PasswordRequired");
                        } else {
                            if (values.oldPassword.length < 6) {
                                errors.oldPassword = t("Register.Validation.PasswordMin");
                            } else if (!/\d/.test(values.oldPassword)) {
                                errors.oldPassword = t("Register.Validation.PasswordDigit");
                            } else if (!/[a-zA-Z]/.test(values.oldPassword)) {
                                errors.oldPassword = t("Register.Validation.PasswordLetter");
                            }
                        }
                        if (!values.newPassword) {
                            errors.newPassword = t("Register.Validation.PasswordRequired");
                        } else {
                            if (values.newPassword.length < 6) {
                                errors.newPassword = t("Register.Validation.PasswordMin");
                            } else if (!/\d/.test(values.newPassword)) {
                                errors.newPassword = t("Register.Validation.PasswordDigit");
                            } else if (!/[a-zA-Z]/.test(values.newPassword)) {
                                errors.newPassword = t("Register.Validation.PasswordLetter");
                            }
                        }
                        if (values.newPassword && values.confirmNewPassword !== values.newPassword) {
                            errors.confirmNewPassword = t("Register.Validation.ConfirmPasswordMatch");
                        }
                        return errors;
                    }}
                    onSubmit={(values, {setSubmitting, resetForm}) => {
                        const data = {
                            oldPassword: values.oldPassword,
                            newPassword: values.newPassword
                        };
                        ChangePasswordAsync(data);
                        setSubmitting(false);
                        resetForm();
                    }}
                >
                    {({handleSubmit, isValid, dirty, isSubmitting}) => (
                        <table className={styles.profileTable}>
                            <tbody>
                            <tr>
                                <td>
                                    <div>{t("Profile.Register")}</div>
                                </td>
                                <td>
                                    <div>{profile ? profile.dateCreated.toString().slice(0, 10) : "-"}</div>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <div>{t("Profile.Name")}</div>
                                </td>
                                <td>
                                    <div>{profile ? profile.fullName : "-"}</div>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <div>{t("Profile.Phone")}</div>
                                </td>
                                <td>
                                    <div>{profile ? profile.phoneNumber : "-"}</div>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <div>{t("Profile.Email")}</div>
                                </td>
                                <td>
                                    <div>{profile ? profile.email : "-"}</div>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <div>{t("Profile.City")}</div>
                                </td>
                                <td>
                                    <div>{profile ? profile.country + ", " + profile.city : "-"}</div>
                                </td>
                            </tr>


                            <tr>
                                <th colSpan={2}>
                                    <div>{t("Profile.ChangePasswordTitle")}</div>
                                </th>
                            </tr>
                            <tr>
                                <td>
                                    <div>{t("Profile.OldPassword")}</div>
                                </td>
                                <td>
                                    <Field name="oldPassword">
                                        {({field}: any) => <input {...field} type="password"/>}
                                    </Field>
                                </td>
                            </tr>
                            <td colSpan={2}>
                                <ErrorMessage name={"oldPassword"} className={styles.error}>
                                    {msg => <div className={styles.error}>{msg}</div>}
                                </ErrorMessage>
                            </td>
                            <tr>
                                <td>
                                    <div>{t("Profile.NewPassword")}</div>
                                </td>
                                <td>
                                    <Field name="newPassword">
                                        {({field, meta}: any) => (
                                            <input {...field} type="password"/>
                                        )}
                                    </Field>
                                </td>
                            </tr>
                            <td colSpan={2}>
                                <ErrorMessage name={"newPassword"} className={styles.error}>
                                    {msg => <div className={styles.error}>{msg}</div>}
                                </ErrorMessage>
                            </td>
                            <tr>
                                <td>
                                    <div>{t("Profile.ConfirmNewPassword")}</div>
                                </td>
                                <td>
                                    <Field name="confirmNewPassword">
                                        {({field, meta}: any) => (
                                            <input {...field} type="password"/>
                                        )}
                                    </Field>
                                </td>
                            </tr>
                            <td colSpan={2}>
                                <ErrorMessage name={"confirmNewPassword"} className={styles.error}>
                                    {msg => <div className={styles.error}>{msg}</div>}
                                </ErrorMessage>
                            </td>
                            <tr>
                                <td colSpan={2} style={{textAlign: "center"}}>
                                    <button type="button" onClick={() => handleSubmit()}
                                            disabled={!(isValid && dirty) || isSubmitting}
                                            className={!(isValid && dirty) || isSubmitting ? styles.disabledButton : ""}>
                                        {t("Profile.ChangePassword")}
                                    </button>
                                </td>
                            </tr>
                            {/*<tr>*/}
                            {/*    <th colSpan={2}>*/}
                            {/*        <div onClick={() => {*/}
                            {/*            LogOut();*/}
                            {/*            navigate("/");*/}
                            {/*        }}>{t("Profile.Logout")}</div>*/}
                            {/*    </th>*/}
                            {/*</tr>*/}
                            </tbody>
                        </table>
                    )}
                </Formik>
            </DropdownMenu>

            <DropdownMenu iconPath={"/images/icons/dropdowns/status.png"} label={t("Status.Status")}
                          preOpened={preOpenDropdown === ProfileDropdowns.PROFILE}
                          preOpenedDropdown={preOpenDropdown}>
                <table className={styles.profileTable}>
                    <tbody>
                    <tr>
                        <td>
                            <div>{t("Status.Role")}</div>
                        </td>
                        <td>
                            <div>{status ? t("Roles." + status.status) : "-"}</div>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <div>{t("Status.RefNumber")}</div>
                        </td>
                        <td>
                            <div>{status ? status.refNumber : "-"}
                                {user.role && user.role.includes("Curator") &&
                                    <div className={styles.svgContainer}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                             viewBox="0 0 24 24"
                                             fill="none" stroke="currentColor" stroke-width="1.5"
                                             stroke-linecap="round"
                                             stroke-linejoin="round"
                                             onClick={async () => {
                                                 const isMobile =
                                                     /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
                                                         navigator.userAgent
                                                     );
                                                 const shareUrl = `${window.location.origin}/register?referrerNumber=${status.refNumber}`;

                                                 if (isMobile && navigator.share) {
                                                     try {
                                                         await navigator.share({
                                                             title: "My referer number!",
                                                             url: shareUrl,
                                                         });
                                                     } catch {
                                                     }
                                                 } else {
                                                     try {
                                                         await navigator.clipboard.writeText(shareUrl);
                                                     } catch {
                                                     }
                                                 }
                                                 setIsRefNumberCopied(true);
                                             }}>
                                            <circle cx="18" cy="5" r="3"/>
                                            <circle cx="6" cy="12" r="3"/>
                                            <circle cx="18" cy="19" r="3"/>
                                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                                        </svg>
                                        <span
                                            className={styles.tooltip}>{isRefNumberCopied ? t("Status.RefererLinkCopied") : t("Status.CopyRefererLink")}</span>
                                    </div>
                                }
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <div>{t("Status.Curator")}</div>
                        </td>
                        <td>
                            <div>{status ? status.curatorName : "-"}</div>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <div>{t("Status.Recommender")}</div>
                        </td>
                        <td>
                            <div>{status ? status.recomendatorNumber : "-"}</div>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <div>{t("Status.GroupId")}</div>
                        </td>
                        <td>
                            <div>{status ? status.groupId : "-"}</div>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <div>{t("Status.GroupCount")}</div>
                        </td>
                        <td>
                            <div>{status ? status.groupCount : "-"}</div>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <div>{t("Status.AcademyMembers")}</div>
                        </td>
                        <td>
                            <div>{status ? status.academyMembers : "-"}</div>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </DropdownMenu>

            {/*<DropdownMenu iconPath={"/images/icons/help-list.png"} label={t("ChangePassword.ChangePassword")}*/}
            {/*              preOpened={preOpenDropdown === ProfileDropdowns.HELP_LIST}>*/}
            {/*    <table className={styles.profileTable}>*/}
            {/*        <tbody>*/}
            {/*        <tr>*/}
            {/*            <td>*/}
            {/*                <div>{t("Status.Role")}</div>*/}
            {/*            </td>*/}
            {/*            <td>*/}
            {/*                <div><Field /></div>*/}
            {/*            </td>*/}
            {/*        </tr>*/}
            {/*        </tbody>*/}
            {/*    </table>*/}
            {/*</DropdownMenu>*/}

            <DropdownMenu iconPath={"/images/icons/dropdowns/help-list.png"} label={t("PracticePosts.HelpList")}
                          preOpened={preOpenDropdown === ProfileDropdowns.HELP_LIST}
                          preOpenedDropdown={preOpenDropdown}>
                <HelpList/>
            </DropdownMenu>

            <DropdownMenu iconPath={"/images/icons/dropdowns/post-practice.png"} label={t("PracticePosts.PracticePosts")}
                          preOpened={preOpenDropdown === ProfileDropdowns.REPORT}
                          preOpenedDropdown={preOpenDropdown}>
                {(postPracticeCalendar && repDate &&
                        <div className="vflex g1" style={{marginBottom: "30px"}}>
                            {/* BLUE Section */}
                            <div className="hflex">
                                <div className={`${stylesPractice.blueBorder} ${stylesPractice.dateCell}`}>
                                    {(postPracticeCalendar.days[0].date.day > 9 ? "" : "0") + postPracticeCalendar.days[0].date.day}.
                                    {(postPracticeCalendar.days[0].date.month > 9 ? "" : "0") + postPracticeCalendar.days[0].date.month}.
                                    {postPracticeCalendar.days[0].date.year}
                                </div>
                                <div
                                    className={`${stylesPractice.blueBorder} ${stylesPractice.ekadashiCell}`}>{t("Ekadasi." + postPracticeCalendar.days[1].ekadashiName.replace(" Ekadasi", ""))}</div>
                                <div
                                    className={`${stylesPractice.blueBorder} ${stylesPractice.ekadashiDescCell}`}>{t("ShortMoons." + postPracticeCalendar.days[0].astrodata.tithi)}</div>
                            </div>
                            <div className="hflex sbf">
                                <div
                                    className={`${stylesPractice.blueBorder} ${stylesPractice.row}`}>{t("PracticePosts.10_1")}</div>
                                <div className={stylesPractice.hint}>
                                    <div
                                        className={
                                            stylesPractice.hintImage + " " +
                                            stylesPractice.blueBorder +
                                            (checkedHints["item_10_1"] ? " " + stylesPractice.checked : "")
                                        }
                                        onClick={() => handleHintClick("item_10_1")}
                                    ></div>
                                    <div className={stylesPractice.percents}>5%</div>
                                </div>
                            </div>
                            <div className="hflex sbf">
                                <div
                                    className={`${stylesPractice.blueBorder} ${stylesPractice.row}`}>{t("PracticePosts.10_2")}</div>
                                <div className={stylesPractice.hint}>
                                    <div
                                        className={
                                            stylesPractice.hintImage + " " +
                                            stylesPractice.blueBorder +
                                            (checkedHints["item_10_2"] ? " " + stylesPractice.checked : "")
                                        }
                                        onClick={() => handleHintClick("item_10_2")}
                                    ></div>
                                    <div className={stylesPractice.percents}>5%</div>
                                </div>
                            </div>
                            <div className="hflex sbf">
                                <div
                                    className={`${stylesPractice.blueBorder} ${stylesPractice.row}`}>{t("PracticePosts.10_3")}</div>
                                <div className={stylesPractice.hint}>
                                    <div
                                        className={
                                            stylesPractice.hintImage + " " +
                                            stylesPractice.blueBorder +
                                            (checkedHints["item_10_3"] ? " " + stylesPractice.checked : "")
                                        }
                                        onClick={() => handleHintClick("item_10_3")}
                                    ></div>
                                    <div className={stylesPractice.percents}>5%</div>
                                </div>
                            </div>

                            {/* RED Section */}
                            <div className="hflex">
                                <div className={`${stylesPractice.redBorder} ${stylesPractice.dateCell}`}>
                                    {(postPracticeCalendar.days[1].date.day > 9 ? "" : "0") + postPracticeCalendar.days[1].date.day}.
                                    {(postPracticeCalendar.days[1].date.month > 9 ? "" : "0") + postPracticeCalendar.days[1].date.month}.
                                    {postPracticeCalendar.days[1].date.year}
                                </div>
                                <div
                                    className={`${stylesPractice.redBorder} ${stylesPractice.ekadashiCell}`}>{t("Ekadasi." + postPracticeCalendar.days[1].ekadashiName.replace(" Ekadasi", ""))}</div>
                                <div
                                    className={`${stylesPractice.redBorder} ${stylesPractice.ekadashiDescCell}`}>{t("ShortMoons." + postPracticeCalendar.days[1].astrodata.tithi)}</div>
                            </div>
                            {["11_1", "11_2", "11_3", "11_4", "11_5", "11_6", "11_7"].map((key, index) => (
                                <div className="hflex sbf" key={index}>
                                    <div
                                        className={`${stylesPractice.redBorder} ${stylesPractice.row}`}>{t(`PracticePosts.${key}`)}</div>
                                    <div className={stylesPractice.hint}>
                                        <div
                                            className={
                                                stylesPractice.hintImage + " " +
                                                stylesPractice.redBorder +
                                                (checkedHints[`item_${key}` as keyof ItemList] ? " " + stylesPractice.checked : "")
                                            }
                                            onClick={() => handleHintClick(`item_${key}` as keyof ItemList)}
                                        ></div>
                                        <div
                                            className={stylesPractice.percents}>{["5%", "10%", "20%", "10%", "10%", "20%", "5%"][index]}</div>
                                    </div>
                                </div>
                            ))}

                            {/* GREEN Section */}
                            <div className="hflex">
                                <div className={`${stylesPractice.greenBorder} ${stylesPractice.dateCell}`}>
                                    {(postPracticeCalendar.days[2].date.day > 9 ? "" : "0") + postPracticeCalendar.days[2].date.day}.
                                    {(postPracticeCalendar.days[2].date.month > 9 ? "" : "0") + postPracticeCalendar.days[2].date.month}.
                                    {postPracticeCalendar.days[2].date.year}
                                </div>
                                <div
                                    className={`${stylesPractice.greenBorder} ${stylesPractice.ekadashiCell}`}>{t("Ekadasi." + postPracticeCalendar.days[1].ekadashiName.replace(" Ekadasi", ""))}</div>
                                <div
                                    className={`${stylesPractice.greenBorder} ${stylesPractice.ekadashiDescCell}`}>{t("ShortMoons." + postPracticeCalendar.days[2].astrodata.tithi)}</div>
                            </div>
                            <div className="hflex sbf">
                                <div
                                    className={`${stylesPractice.greenBorder} ${stylesPractice.row}`}>{t("PracticePosts.12_1")}</div>
                                <div className={stylesPractice.hint}>
                                    <div
                                        className={
                                            stylesPractice.hintImage + " " +
                                            stylesPractice.greenBorder +
                                            (checkedHints["item_12_1"] ? " " + stylesPractice.checked : "")
                                        }
                                        onClick={() => handleHintClick("item_12_1")}
                                    ></div>
                                    <div className={stylesPractice.percents}>5%</div>
                                </div>
                            </div>

                            {/* Progress Block */}
                            <div className={stylesPractice.practiceFrame}>
                                {canSendReport ? isPostReportChanged ?
                                        <div
                                            className={stylesPractice.progressBar}
                                            onClick={handleSendReportClick}
                                        >
                                            <div
                                                className={stylesPractice.progressText}>{t("PracticePosts.SendReport")}</div>
                                        </div>
                                        :
                                        <div className={stylesPractice.progressBar + " " + stylesPractice.disabled}>
                                            {isPostReportTouched ?
                                                <div
                                                    className={stylesPractice.progressText}>{t("PracticePosts.ReportSent")}</div>
                                                :
                                                <div
                                                    className={stylesPractice.progressText}>{t("PracticePosts.SendReport")}</div>
                                            }
                                        </div>
                                    :
                                    <div className={stylesPractice.progressBar + " " + stylesPractice.disabled}>
                                        <div
                                            className={stylesPractice.progressText}>{t("PracticePosts.CanSendReportFrom")} {`${String(repDate!.getDate()).padStart(2, '0')}-${String(repDate!.getMonth() + 1).padStart(2, '0')}`}</div>
                                    </div>
                                }
                                <div className={stylesPractice.progressDescription}>
                                    {t("PracticePosts.ProgressDescription")}
                                </div>
                                <div>
                                    <ProgressBar value={checkedWeightSum}/>
                                </div>
                            </div>
                        </div>)
                    ||
                    <div>Loading...</div>
                }
            </DropdownMenu>

            <DropdownMenu iconPath={"/images/icons/dropdowns/results.png"} label={t("PracticePosts.Results")}
                          preOpened={preOpenDropdown === ProfileDropdowns.RESULTS}
                          preOpenedDropdown={preOpenDropdown}>
                <PostResult/>
            </DropdownMenu>
            {/*<DropdownMenu iconPath={"/images/icons/profile.png"} label={t("PostResults.PostResults")}>*/}
            {/*<div></div>*/}
            {/*</DropdownMenu>*/}
        </div>
    );
}

export default Profile;
